/**
 * MediaLightbox
 *
 * Fullscreen overlay for viewing images and videos.
 * Supports gallery navigation with arrow keys.
 */

import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DownloadFile } from '@/hooks/useDownloads';
import { formatBytes } from '@/hooks/useDownloads';

interface MediaLightboxProps {
	files: DownloadFile[];
	initialIndex: number;
	onClose: () => void;
}

export function MediaLightbox({ files, initialIndex, onClose }: MediaLightboxProps) {
	const [index, setIndex] = useState(initialIndex);
	const file = files[index];
	const isImage = file.mimeType.startsWith('image/');
	const isVideo = file.mimeType.startsWith('video/');
	const hasMultiple = files.length > 1;

	const goNext = useCallback(() => {
		setIndex((i) => (i + 1) % files.length);
	}, [files.length]);

	const goPrev = useCallback(() => {
		setIndex((i) => (i - 1 + files.length) % files.length);
	}, [files.length]);

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
			} else if (e.key === 'ArrowRight' || e.key === 'l') {
				e.preventDefault();
				goNext();
			} else if (e.key === 'ArrowLeft' || e.key === 'h') {
				e.preventDefault();
				goPrev();
			}
		}
		window.addEventListener('keydown', handleKeyDown);
		// Prevent body scroll
		document.body.style.overflow = 'hidden';
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [onClose, goNext, goPrev]);

	function handleBackdropClick(e: React.MouseEvent) {
		if (e.target === e.currentTarget) onClose();
	}

	function handleDownload() {
		const a = document.createElement('a');
		a.href = file.url;
		a.download = file.filename;
		a.click();
	}

	return createPortal(
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
			onClick={handleBackdropClick}
		>
			{/* Close button */}
			<button
				type="button"
				onClick={onClose}
				className="absolute top-4 right-4 z-10 p-2 text-white/60 hover:text-white transition-colors"
			>
				<X className="size-6" />
			</button>

			{/* Counter + info */}
			<div className="absolute top-4 left-4 z-10 flex items-center gap-3">
				{hasMultiple && (
					<span className="text-white/80 font-mono text-sm">
						{index + 1} / {files.length}
					</span>
				)}
				<span className="text-white/40 font-mono text-xs">
					{file.filename} - {formatBytes(file.sizeBytes)}
				</span>
			</div>

			{/* Download button */}
			<button
				type="button"
				onClick={handleDownload}
				className="absolute bottom-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white rounded font-mono text-xs transition-colors"
			>
				<Download className="size-3.5" />
				Download
			</button>

			{/* Previous button */}
			{hasMultiple && (
				<button
					type="button"
					onClick={(e) => { e.stopPropagation(); goPrev(); }}
					className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/40 hover:text-white transition-colors"
				>
					<ChevronLeft className="size-8" />
				</button>
			)}

			{/* Next button */}
			{hasMultiple && (
				<button
					type="button"
					onClick={(e) => { e.stopPropagation(); goNext(); }}
					className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/40 hover:text-white transition-colors"
				>
					<ChevronRight className="size-8" />
				</button>
			)}

			{/* Media content */}
			<div className="max-w-[95vw] max-h-[90vh] flex items-center justify-center">
				{isImage && (
					<img
						src={file.url}
						alt={file.filename}
						className="max-w-full max-h-[90vh] object-contain select-none"
						draggable={false}
					/>
				)}
				{isVideo && (
					<video
						src={file.url}
						controls
						autoPlay
						className="max-w-full max-h-[90vh]"
					>
						<track kind="captions" />
					</video>
				)}
			</div>
		</div>,
		document.body
	);
}
